import { TWidget } from 'widgets/Widget';

/**
 * @param Widget Base widget for extending
 * @returns RefinementsPanelToggle instance
 */
function RefinementsPanelToggleClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory search
     * @class RefinementsPanelToggle
     * @augments Widget
     * @classdesc Widget, which triggers touch/mobile refinements panel to open/close
     * @property {string} data-widget - Widget name `refinementsPanelToggle`
     * @property {string} data-event-click - Event handler method for click event
     *
     * @example <caption>Example of RefinementsPanelToggle widget usage</caption>
     * <button
     *     class="b-plp_actions-refinements_toggle"
     *     aria-controls="refinements-panel"
     *     title="${Resource.msg('refinements.filter', 'search', null)}"
     *     type="button"
     *     data-widget="refinementsPanelToggle"
     *     data-event-click.prevent.stop="openRefinementsPanel"
     *     data-event-keydown="handleKeydown"
     *     data-tau="refinements_open"
     * >
     *     ${Resource.msg('refinements.filter', 'search', null)}
     * </button>
     */
    class RefinementsPanelToggle extends Widget {
        /**
         * @description Init widget logic
         * @listens "refinement.panel.close"
         */
        init() {
            this.eventBus().on('refinement.panel.close', 'closeRefinementsPanel');
        }

        /**
         * @description Open panel
         * @emits "refinement.panel.open"
         */
        openRefinementsPanel() {
            /**
             * @description Event to open refinement panel
             * @event "refinement.panel.open"
             */
            this.ref('self').attr('aria-expanded', 'true');
            this.eventBus().emit('refinement.panel.open');
        }

        /**
         * @description Close panel
         */
        closeRefinementsPanel() {
            this.ref('self').focus();
            this.ref('self').attr('aria-expanded', 'false');
        }

        /**
         * @description Keydown Event handler
         * @listens dom#keydown
         * @param _ Source of keydown event
         * @param event  Event object
         */
        handleKeydown(_: HTMLElement, event: KeyboardEvent) {
            let preventEventActions = false;

            switch (event.key) {
                case 'Enter':
                case ' ':
                case 'ArrowDown':
                    this.openRefinementsPanel();
                    preventEventActions = true;
                    break;

                default:
                    break;
            }

            if (preventEventActions) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }

    return RefinementsPanelToggle;
}

export type TRefinementsPanelToggle = ReturnType<typeof RefinementsPanelToggleClassCreator>;

export type TRefinementsPanelToggleInstance = InstanceType<TRefinementsPanelToggle>;

export default RefinementsPanelToggleClassCreator;
